import images from 'assets/images';
import { IDNAPaymentsType } from 'models/payment';
import { Helmet } from 'react-helmet';
import type { IChargingProgressProps } from '../../types/propTypes';
import ChargingGraph from './ChargingGraph';
import { useChargingProgressHook } from './hooks/ChargingProgressHook';

declare global {
    interface Window {
        DNAPayments: IDNAPaymentsType;
    }
}

const ChargingProgress = (props: IChargingProgressProps) => {
    const {
        t,
        isPaymentRetry,
        isSessionCompleted,
        code,
        sessionDetails,
        graphData,
        showCompletedMessage,
        showErrorMessage,
        showWaitingMessage,
        paymentDetails,
        paymentFailMessage,
        amountToPay,
        thankYouMessage,
        showTryAgain,
        showPayWithDifferentCard,
        settlementAmount,
        allPaymentsFailed,
        allPaymentsFailedMessage,
        handleStopCharge,
        handleDownloadInvoice,
        handlePayWithDifferentCard,
        handleTryAgain,
        backbtnclick,
    } = useChargingProgressHook(props);

    return (
        <>
            <Helmet>
                <title>{t<string>('chargingProgress.title')}</title>
            </Helmet>

            <div className="charging-progress">
                {!(isPaymentRetry || isSessionCompleted) && (
                    <>
                        <div className="station-title-div">
                            <div className="marker-img-div">
                                <img src={images.icMarkerBigIcon} className="marker-img" alt="marker" />
                            </div>
                            <div className="station-name">
                                <h6 className="station-title">
                                    {sessionDetails?.station_name
                                        ? `${sessionDetails?.station_name} (${code})`
                                        : `${code}`}
                                </h6>
                            </div>
                        </div>
                        {graphData.length <= 1 ? (
                            <div className="info-text">
                                <span>{t<string>('chargingProgress.label.info')}</span>
                            </div>
                        ) : (
                            <div className="graph-div">
                                <ChargingGraph graphData={graphData} />
                            </div>
                        )}
                        <div className="row no-gutters justify-content-between charge-details-wrapper">
                            <div className="col-12 charge-details-card">
                                <div className="detail-box col-6 box-border-right box-border-bottom">
                                    <div>
                                        <span className="value-span">{sessionDetails?.charge_duration || 0}</span>
                                    </div>
                                    <div>
                                        <label className="info-label">
                                            {t<string>('chargingProgress.label.chargeDuration')}
                                        </label>
                                    </div>
                                </div>
                                <div className="detail-box col-6 box-border-bottom">
                                    <div>
                                        <span className="value-span">
                                            {sessionDetails?.total_power_consumed
                                                ? sessionDetails.total_power_consumed.toFixed(2)
                                                : '0.00'}
                                        </span>
                                        <span className="info-span">{t<string>('chargingProgress.label.kwh')}</span>
                                    </div>
                                    <div>
                                        <label className="info-label">
                                            {t<string>('chargingProgress.label.energyConsumed')}
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 charge-details-card">
                                <div className="detail-box col-6 box-border-right">
                                    <div>
                                        <span className="value-span">{sessionDetails?.charge_percentage || 0}</span>
                                        <span className="info-span">
                                            {t<string>('chargingProgress.label.percentage')}
                                        </span>
                                    </div>
                                    <div>
                                        <label className="info-label">
                                            {t<string>('chargingProgress.label.chargeLevel')}
                                        </label>
                                    </div>
                                </div>
                                <div className="detail-box col-6">
                                    <div>
                                        <span className="value-span">
                                            {sessionDetails?.cost != null &&
                                            !isNaN(Number(sessionDetails.cost)) &&
                                            Number(sessionDetails.cost) > 0
                                                ? `£${sessionDetails.cost}`
                                                : '-'}
                                        </span>
                                    </div>
                                    <div>
                                        <label className="info-label">{t<string>('chargingProgress.label.cost')}</label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {!(showCompletedMessage || showErrorMessage) && sessionDetails !== null && (
                            <div className="clearfix mt-2 stop-charge-div">
                                <button
                                    type="button"
                                    className="btn btn-primary mr-2 stop-charge-btn"
                                    onClick={() => handleStopCharge()}
                                >
                                    <span className="stop-charge-span">
                                        {t<string>('chargingProgress.label.stopCharge')}
                                    </span>
                                    <img src={images.icChargeMedIcon} className="stop-charge-img" alt="marker" />
                                </button>
                            </div>
                        )}
                    </>
                )}

                {showWaitingMessage && (
                    <div className="info-text">
                        <span>{t<string>('chargingProgress.label.waitingMessage')}</span>
                    </div>
                )}

                {showErrorMessage && !showWaitingMessage && (
                    <>
                        <div className="charging-error mb-3">
                            <div className="session-complete-div">
                                <img src={images.icErrorIcon} className="error-img" alt="success" />
                                <span className="session-error-span ">{t<string>('chargingProgress.label.error')}</span>
                            </div>
                            <div className="session-complete-message">
                                <span>{t<string>('chargingProgress.label.unableToConnect')}</span>
                            </div>
                        </div>
                        <div className="clearfix mt-2 pb-3">
                            <button type="button" className="btn btn-primary" onClick={() => backbtnclick()}>
                                <span>{t<string>('paymentFail.button.back')}</span>
                            </button>
                        </div>
                    </>
                )}

                {!showWaitingMessage && paymentDetails !== null && (
                    <>
                        {(showCompletedMessage || isSessionCompleted) && (
                            <>
                                <div className="charging-complete mb-4">
                                    <div className="session-complete-div">
                                        <img
                                            src={images.icStopChargeSuccessIcon}
                                            className="marker-img"
                                            alt="success"
                                        />
                                        <span className="session-complete-span ">
                                            {t<string>('chargingProgress.label.sessionComplete')}
                                        </span>
                                    </div>
                                    <div className="session-complete-message">
                                        <span>
                                            {paymentFailMessage
                                                ? t<string>('chargingProgress.label.thankYouForCharging')
                                                : thankYouMessage}
                                        </span>
                                    </div>
                                </div>
                            </>
                        )}
                        {paymentDetails.pre_auth_payment_status === 'SETTLED' &&
                            paymentDetails.settlement_payment_status === 'SETTLED' && (
                                <>
                                    {isPaymentRetry && (
                                        <div className="row align-items-center">
                                            <div className="col">
                                                <div className="mesg-box text-center">
                                                    <img
                                                        src={images.successSVG}
                                                        alt="success"
                                                        className="success-img"
                                                    />
                                                    <h2 className="text-success">
                                                        {t<string>('paymentSuccess.label')}
                                                    </h2>
                                                    <p>{t<string>('paymentSuccess.Successlabel')}</p>
                                                    <div className="charging-complete mb-4">
                                                        <div className="session-complete-message">
                                                            <span>{thankYouMessage}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="clearfix mt-2 stop-charge-div">
                                        <button
                                            type="button"
                                            className="btn btn-primary mr-2 stop-charge-btn"
                                            onClick={() => handleDownloadInvoice()}
                                        >
                                            <span className="stop-charge-span">
                                                {t<string>('chargingProgress.label.downloadInvoice')}
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary stop-charge-btn"
                                            onClick={() => backbtnclick()}
                                        >
                                            <span className="stop-charge-span">
                                                {t<string>('paymentFail.button.back')}
                                            </span>
                                        </button>
                                    </div>
                                </>
                            )}

                        {(paymentDetails.settlement_payment_status === 'FAILED' ||
                            paymentDetails.settlement_payment_status === 'SETTLEMENT_FAILED') && (
                            <>
                                {isPaymentRetry && (
                                    <div className="row align-items-center">
                                        <div className="col">
                                            <div className="mesg-box text-center">
                                                <img src={images.errorIconSVG} alt="error" className="success-img" />
                                                <h2 className="error-color">{t<string>('paymentFail.title')}</h2>
                                                <p>{t<string>('paymentFail.failureLabel')}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="instruction-title">
                                    <h6 className="sub-title">{t<string>('chargingProgress.label.paymentDetails')}</h6>
                                </div>
                                <div className="charging-error mb-4">
                                    <div className="session-complete-div">
                                        <img src={images.icErrorIcon} className="error-img" alt="error" />
                                        <span className="session-error-span ">
                                            {t<string>('chargingProgress.label.paymentFailedTitle')}
                                        </span>
                                    </div>
                                    <div className="session-complete-message">
                                        <span>{allPaymentsFailed ? allPaymentsFailedMessage : paymentFailMessage}</span>
                                    </div>
                                </div>
                                <div className="settlement-failed">
                                    {showTryAgain && settlementAmount && settlementAmount < 0 && (
                                        <div className="clearfix mt-2">
                                            <button
                                                type="button"
                                                className="btn btn-primary mr-2 settlement-failed-btn"
                                                onClick={() => handleTryAgain()}
                                            >
                                                <span className="stop-charge-span">
                                                    {t<string>('chargingProgress.label.tryAgain')}
                                                </span>
                                            </button>
                                        </div>
                                    )}
                                    {showPayWithDifferentCard && (
                                        <div className="clearfix mt-2 stop-charge-div">
                                            <button
                                                type="button"
                                                className="btn btn-primary mr-2 settlement-failed-btn"
                                                onClick={() => handlePayWithDifferentCard()}
                                            >
                                                <span className="stop-charge-span">
                                                    {t<string>('chargingProgress.label.payWithDifferentCard').replace(
                                                        '##AMOUNT##',
                                                        amountToPay != null ? `£${amountToPay.toString()}` : '',
                                                    )}
                                                </span>
                                            </button>
                                        </div>
                                    )}
                                    {allPaymentsFailed && (
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={() => backbtnclick()}
                                        >
                                            <span>{t<string>('paymentFail.button.back')}</span>
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </>
    );
};
export default ChargingProgress;
